import { useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { LAND_DOTS } from "./geoData";
import { R, ll, PLACES, ROUTES, arcCurve, routeCurve, glowTex, clamp01, ease } from "./geo";
import { Projector } from "./Projector";

const D = Math.PI / 180;
const ADD = THREE.AdditiveBlending;
const shortest = (a) => ((((a + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI;

function Land() {
  const geo = useMemo(() => {
    const p = new Float32Array((LAND_DOTS.length / 2) * 3);
    for (let i = 0; i < LAND_DOTS.length; i += 2) {
      const v = ll(LAND_DOTS[i], LAND_DOTS[i + 1], R * 1.003);
      p.set([v.x, v.y, v.z], (i / 2) * 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(p, 3));
    return g;
  }, []);
  return (
    <points geometry={geo}>
      <pointsMaterial size={0.04} map={glowTex()} color="#f0a25a" transparent depthWrite={false} opacity={0.95} blending={ADD} />
    </points>
  );
}

function Graticule() {
  const geo = useMemo(() => {
    const pts = [];
    for (let lat = -60; lat <= 60; lat += 20)
      for (let lon = -180; lon < 180; lon += 4) pts.push(ll(lat, lon, R * 1.001), ll(lat, lon + 4, R * 1.001));
    for (let lon = -180; lon < 180; lon += 20)
      for (let lat = -84; lat < 84; lat += 4) pts.push(ll(lat, lon, R * 1.001), ll(lat + 4, lon, R * 1.001));
    return new THREE.BufferGeometry().setFromPoints(pts);
  }, []);
  return (
    <lineSegments geometry={geo}>
      <lineBasicMaterial color="#ffb347" transparent opacity={0.06} depthWrite={false} />
    </lineSegments>
  );
}

const vert = `varying vec3 vN; varying vec3 vP; void main(){ vN=normalize(normalMatrix*normal); vec4 mv=modelViewMatrix*vec4(position,1.); vP=mv.xyz; gl_Position=projectionMatrix*mv; }`;

function Core() {
  const mat = useMemo(() => new THREE.ShaderMaterial({
    uniforms: { c: { value: new THREE.Color("#ff7a1a") } },
    vertexShader: vert,
    fragmentShader: `uniform vec3 c; varying vec3 vN; varying vec3 vP; void main(){ float f=pow(1.-max(dot(normalize(-vP),vN),0.),2.6); gl_FragColor=vec4(vec3(0.025,0.022,0.028)+c*f*0.55,1.); }`,
  }), []);
  return <mesh material={mat}><sphereGeometry args={[R, 96, 96]} /></mesh>;
}

function Atmosphere() {
  const mat = useMemo(() => new THREE.ShaderMaterial({
    uniforms: { c: { value: new THREE.Color("#ff8a2a") } },
    vertexShader: vert,
    fragmentShader: `uniform vec3 c; varying vec3 vN; varying vec3 vP; void main(){ float d=abs(dot(normalize(-vP),vN)); float f=pow(1.-d,2.2)*smoothstep(0.,0.35,d); gl_FragColor=vec4(c,f*0.6); }`,
    transparent: true, blending: ADD, side: THREE.BackSide, depthWrite: false,
  }), []);
  return <mesh material={mat}><sphereGeometry args={[R * 1.16, 64, 64]} /></mesh>;
}

function Stars() {
  const geo = useMemo(() => {
    const p = new Float32Array(1800 * 3);
    for (let i = 0; i < 1800; i++) {
      const v = new THREE.Vector3().randomDirection().multiplyScalar(25 + Math.random() * 30);
      p.set([v.x, v.y, v.z], i * 3);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(p, 3));
    return g;
  }, []);
  const ref = useRef();
  useFrame((_, dt) => { ref.current.rotation.y += dt * 0.004; });
  return (
    <points ref={ref} geometry={geo}>
      <pointsMaterial size={0.09} color="#ffe6cc" transparent opacity={0.5} depthWrite={false} />
    </points>
  );
}

function Marker({ place, objRef, color = "#ffb347", phase = 0 }) {
  const pos = useMemo(() => ll(place.lat, place.lon, R * 1.012), [place]);
  const quat = useMemo(() => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), pos.clone().normalize()), [pos]);
  const ring = useRef();
  useFrame((s) => {
    const t = (s.clock.elapsedTime * 0.6 + phase) % 1;
    ring.current.scale.setScalar(0.6 + t * 2.4);
    ring.current.material.opacity = (1 - t) * 0.9;
  });
  return (
    <group position={pos} quaternion={quat} ref={objRef}>
      <sprite scale={[0.3, 0.3, 1]}>
        <spriteMaterial map={glowTex()} color={color} blending={ADD} depthWrite={false} transparent />
      </sprite>
      <mesh ref={ring}>
        <ringGeometry args={[0.05, 0.062, 48]} />
        <meshBasicMaterial color={color} transparent side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <mesh><circleGeometry args={[0.022, 24]} /><meshBasicMaterial color="#fff6e8" /></mesh>
    </group>
  );
}

function Arc({ from, to, wps, color = "#ffb347", lift = 0.28, radius = 0.01, delay = 0, pulses = 3, speed = 0.3 }) {
  const curve = useMemo(() => (wps ? routeCurve(ROUTES[wps]) : arcCurve(PLACES[from], PLACES[to], lift)), [wps, from, to, lift]);
  const geos = useMemo(() => [radius, radius * 3.2].map((r) => new THREE.TubeGeometry(curve, 240, r, 8, false)), [curve, radius]);
  const t0 = useRef(null);
  const pRefs = useRef([]);
  useFrame((s) => {
    if (t0.current === null) t0.current = s.clock.elapsedTime;
    const el = s.clock.elapsedTime - t0.current - delay;
    const p = ease(clamp01(el / 1.8));
    geos.forEach((g) => g.setDrawRange(0, Math.floor((p * g.index.count) / 3) * 3));
    pRefs.current.forEach((m, i) => {
      if (!m) return;
      m.visible = p > 0.98;
      m.position.copy(curve.getPointAt((((el * speed + i / pulses) % 1) + 1) % 1));
    });
  });
  return (
    <group>
      <mesh geometry={geos[0]}><meshBasicMaterial color={color} transparent opacity={0.95} blending={ADD} depthWrite={false} /></mesh>
      <mesh geometry={geos[1]}><meshBasicMaterial color={color} transparent opacity={0.18} blending={ADD} depthWrite={false} /></mesh>
      {Array.from({ length: pulses }).map((_, i) => (
        <sprite key={i} ref={(el) => (pRefs.current[i] = el)} scale={[0.16, 0.16, 1]} visible={false}>
          <spriteMaterial map={glowTex()} color="#fff1dc" blending={ADD} transparent depthWrite={false} />
        </sprite>
      ))}
    </group>
  );
}

function Ships({ route, delay = 0, color = "#6cc5ff" }) {
  const curve = useMemo(() => routeCurve(ROUTES[route], 1.01), [route]);
  const refs = useRef([]);
  const t0 = useRef(null);
  const [p, tg] = useMemo(() => [new THREE.Vector3(), new THREE.Vector3()], []);
  useFrame((s) => {
    if (t0.current === null) t0.current = s.clock.elapsedTime;
    const el = s.clock.elapsedTime - t0.current - delay - 1.6;
    refs.current.forEach((m, i) => {
      if (!m) return;
      m.visible = el > 0;
      const t = (Math.max(el, 0) * 0.07 + i / 2) % 1;
      curve.getPointAt(t, p);
      curve.getTangentAt(t, tg);
      m.position.copy(p);
      m.up.copy(p).normalize();
      m.lookAt(tg.add(p));
    });
  });
  return (
    <group>
      <Arc wps={route} color={color} radius={0.005} pulses={0} delay={delay} />
      {[0, 1].map((i) => (
        <group key={i} ref={(el) => (refs.current[i] = el)} visible={false}>
          <mesh><boxGeometry args={[0.024, 0.016, 0.085]} /><meshBasicMaterial color="#e8f6ff" /></mesh>
          <sprite scale={[0.22, 0.22, 1]}>
            <spriteMaterial map={glowTex()} color={color} blending={ADD} transparent depthWrite={false} />
          </sprite>
        </group>
      ))}
    </group>
  );
}

function Rig({ cfg, centerRef, children }) {
  const outer = useRef();
  const inner = useRef();
  const spin = useRef(0);
  const { camera } = useThree();
  useFrame((s, dt) => {
    const k = 1 - Math.pow(0.12, dt);
    if (cfg.spin) spin.current += dt * 0.07;
    const drift = cfg.spin ? 0 : Math.sin(s.clock.elapsedTime * 0.25) * 2;
    const ty = -Math.PI / 2 - (cfg.lon + drift) * D - (cfg.spin ? spin.current : 0);
    inner.current.rotation.y += shortest(ty - inner.current.rotation.y) * k;
    outer.current.rotation.x += (cfg.lat * D - outer.current.rotation.x) * k;
    outer.current.position.x += ((cfg.x ?? 0) - outer.current.position.x) * k;
    outer.current.position.y += ((cfg.y ?? 0) - outer.current.position.y) * k;
    camera.position.z += ((cfg.dist ?? 6.5) - camera.position.z) * k;
  });
  return (
    <group ref={(el) => { outer.current = el; centerRef.current = el; }}>
      <group ref={inner}>{children}</group>
    </group>
  );
}

export default function Globe({ cfg, slideKey }) {
  const objs = useRef([]);
  const labels = useRef([]);
  const center = useRef();
  const markers = cfg.markers || [];
  objs.current.length = markers.length;
  labels.current.length = markers.length;
  return (
    <div className="globe-layer" style={{ opacity: cfg.opacity ?? 1 }} data-testid="globe-layer">
      <Canvas camera={{ position: [0, 0, 9], fov: 40 }} dpr={[1, 2]} gl={{ antialias: true, alpha: true }}>
        <Rig cfg={cfg} centerRef={center}>
          <Core />
          <Graticule />
          <Land />
          <Atmosphere />
          <group key={slideKey}>
            {markers.map((m, i) => (
              <Marker key={m} place={PLACES[m]} phase={i * 0.2} color={cfg.markerColor} objRef={(el) => (objs.current[i] = el)} />
            ))}
            {(cfg.arcs || []).map((a, i) => <Arc key={i} delay={0.3 + i * 0.25} {...a} />)}
            {(cfg.ships || []).map((r, i) => <Ships key={r} route={r} delay={0.3 + i * 0.4} />)}
          </group>
        </Rig>
        <Stars />
        <Projector targets={objs} labels={labels} centerRef={center} />
      </Canvas>
      <div className="globe-labels">
        {markers.map((m, i) => (
          <div key={slideKey + m} ref={(el) => (labels.current[i] = el)} className="globe-label" data-testid={`globe-label-${m}`}>
            <span>{PLACES[m].name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
